import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from './api/axios';
import Header from './Header';
import { useAuth } from './AuthContext';
import { FaExclamationTriangle, FaSyncAlt, FaArrowLeft } from 'react-icons/fa';

// --- Helper Components & Icons ---

const LoadingSpinner = () => (
    <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading KPIs...</p>
    </div>
);

const ErrorMessage = ({ message }) => (
    <div className="form-feedback error">
        <p>{message}</p>
    </div>
);

// --- NEW: Helper to format interface names nicely ---
const formatInterfaceName = (name) => {
    if (!name) return 'N/A';
    return name.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
};

const StatusOverviewTable = ({ overviewData }) => {
    if (!overviewData || overviewData.length === 0) {
        return <p className="info-message">No interface status data to display.</p>;
    }

    const getStatusClass = (status) => {
        if (!status) return 'status-unknown';
        switch (status.toLowerCase()) {
            case 'completed': return 'status-completed';
            case 'in progress': return 'status-in-progress';
            case 'failed': return 'status-failed-validation';
            case 'failed validation': return 'status-failed-validation';
            default: return 'status-not-started';
        }
    };

    return (
        <div className="table-wrapper">
            <table className="styled-table overview-table">
                <thead>
                    <tr>
                        <th>Drop</th>
                        <th>Category</th>
                        <th>Interface</th>
                        <th>Overall Status</th>
                        <th>Sample Delivery</th>
                        <th>Tech Valid.</th>
                        <th>Full Extraction</th>
                        <th>Integration</th>
                        <th>RELEX Upload</th>
                        <th>Func. Valid.</th>
                    </tr>
                </thead>
                <tbody>
                    {overviewData.map(item => {
                        const taskStatuses = item.tasks.reduce((acc, task) => {
                            acc[task.task_name] = task.status;
                            return acc;
                        }, {});

                        return (
                            <tr key={item.interface_name}>
                                <td>{item.drop_number || 'N/A'}</td>
                                <td>{item.integration_category || 'N/A'}</td>
                                {/* --- APPLIED FIX: Formatting the interface name --- */}
                                <td>{formatInterfaceName(item.interface_name)}</td>
                                <td><span className={`status-badge ${getStatusClass(item.overall_status)}`}>{item.overall_status}</span></td>
                                <td><span className={`status-badge ${getStatusClass(taskStatuses["Sample Delivery"])}`}>{taskStatuses["Sample Delivery"] || 'N/A'}</span></td>
                                <td><span className={`status-badge ${getStatusClass(taskStatuses["Technical Validation"])}`}>{taskStatuses["Technical Validation"] || 'N/A'}</span></td>
                                <td><span className={`status-badge ${getStatusClass(taskStatuses["Full Extraction"])}`}>{taskStatuses["Full Extraction"] || 'N/A'}</span></td>
                                <td><span className={`status-badge ${getStatusClass(taskStatuses["Connect Integration"])}`}>{taskStatuses["Connect Integration"] || 'N/A'}</span></td>
                                <td><span className={`status-badge ${getStatusClass(taskStatuses["Upload in RELEX"])}`}>{taskStatuses["Upload in RELEX"] || 'N/A'}</span></td>
                                <td><span className={`status-badge ${getStatusClass(taskStatuses["Functional Validation"])}`}>{taskStatuses["Functional Validation"] || 'N/A'}</span></td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};


const KpiOverallProgressBar = ({ progress }) => {
    if (!progress) return null;

    const {
        completed_percent, in_progress_percent, failed_percent, not_started_percent,
        completed_count, in_progress_count, failed_count, not_started_count,
        total_interfaces, delayed_count,
        in_progress_delayed_count = 0,
        not_started_delayed_count = 0,
        failed_delayed_count = 0
    } = progress;

    const statuses = [
        { label: 'Completed', colorClass: 'green', count: completed_count },
        { label: 'In Progress', colorClass: 'yellow', count: in_progress_count, delayed: in_progress_delayed_count },
        { label: 'Failed', colorClass: 'red', count: failed_count, delayed: failed_delayed_count },
        { label: 'Not Started', colorClass: 'blue', count: not_started_count, delayed: not_started_delayed_count },
    ];

    return (
        <div className="overall-progress-wrapper content-card">
            <div className="kpi-summary-header">
                <div className="summary-item">
                    <span className="summary-label">Total Interfaces</span>
                    <span className="summary-value total">{total_interfaces}</span>
                </div>
                <div className="summary-item">
                    <span className="summary-label">Delayed</span>
                    <span className="summary-value delayed">{delayed_count}</span>
                </div>
            </div>
            <div className="progress-bar-container">
                <div className="progress-bar-segment green" style={{ width: `${completed_percent}%` }} title={`Completed: ${completed_percent.toFixed(1)}%`}>
                    {completed_percent > 7 && `${completed_count} (${completed_percent.toFixed(1)}%)`}
                </div>
                <div className="progress-bar-segment yellow" style={{ width: `${in_progress_percent}%` }} title={`In Progress: ${in_progress_percent.toFixed(1)}%`}>
                    {in_progress_percent > 7 && `${in_progress_count} (${in_progress_percent.toFixed(1)}%)`}
                </div>
                <div className="progress-bar-segment red" style={{ width: `${failed_percent}%` }} title={`Failed: ${failed_percent.toFixed(1)}%`}>
                    {failed_percent > 7 && `${failed_count} (${failed_percent.toFixed(1)}%)`}
                </div>
                <div className="progress-bar-segment blue" style={{ width: `${not_started_percent}%` }} title={`Not Started: ${not_started_percent.toFixed(1)}%`}>
                    {not_started_percent > 7 && `${not_started_count} (${not_started_percent.toFixed(1)}%)`}
                </div>
            </div>
            <div className="progress-legend">
                {statuses.map(status => (
                    <div key={status.label} className="legend-item">
                        <div className="legend-main">
                            <span className={`legend-dot ${status.colorClass}`}></span>
                            <span className="legend-label">{status.label} ({status.count})</span>
                        </div>
                        {status.delayed > 0 && (
                            <div className="legend-sublabel">Delayed ({status.delayed})</div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

const KpiDropSummaryTable = ({ drop }) => (
    <div className="drop-summary-card content-card" style={{ display: 'flex', flexDirection: 'column' }}>
        {/* --- APPLIED FIX: Added a fixed bottom margin here to guarantee spacing --- */}
        <div style={{ flexGrow: 1, marginBottom: '1.5rem' }}>
            <h3 className="drop-summary-title">{drop.drop_name} ({drop.total_interfaces} interfaces)</h3>
            <table className="drop-summary-table">
                <tbody>
                    <tr>
                        <td><span className="legend-dot green" style={{ marginRight: '0.5vw' }}></span>Completed</td>
                        <td>{drop.completed_count}</td>
                        <td>{drop.completed_percent.toFixed(1)}%</td>
                    </tr>
                    <tr>
                        <td><span className="legend-dot yellow" style={{ marginRight: '0.5vw' }}></span>In Progress</td>
                        <td>{drop.in_progress_count}</td>
                        <td>{drop.in_progress_percent.toFixed(1)}%</td>
                    </tr>
                    <tr>
                        <td><span className="legend-dot red" style={{ marginRight: '0.5vw' }}></span>Failed</td>
                        <td>{drop.failed_count}</td>
                        <td>{drop.failed_percent.toFixed(1)}%</td>
                    </tr>
                    <tr>
                        <td><span className="legend-dot blue" style={{ marginRight: '0.5vw' }}></span>Not Started</td>
                        <td>{drop.not_started_count}</td>
                        <td>{drop.not_started_percent.toFixed(1)}%</td>
                    </tr>
                </tbody>
            </table>
        </div>

        {/* --- APPLIED FIX: Changed marginTop to 'auto' to prevent overflow --- */}
        <div className="delayed-metrics-footer" style={{
            marginTop: 'auto',
            padding: '0.75rem 1rem',
            backgroundColor: 'var(--input-bg)',
            border: '1px solid var(--border-color)',
            borderLeft: '4px solid var(--danger-color)',
            borderRadius: '6px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem', color: 'var(--text-color)', fontWeight: '600' }}>
                <FaExclamationTriangle style={{ color: 'var(--danger-color)', marginRight: '0.5rem' }} />
                Delayed
            </div>
            <div style={{ textAlign: 'right' }}>
                <span style={{ fontWeight: '700', color: 'var(--text-color)', marginRight: '0.5rem', fontSize: '1.05rem' }}>{drop.delayed_count}</span>
                <span style={{ color: 'var(--light-text)', fontSize: '0.85rem' }}>({drop.delayed_percent.toFixed(1)}%)</span>
            </div>
        </div>
    </div>
);

const KpiWeeklyComparison = ({ comparison }) => {
    if (!comparison) return null;
    const { current_week, last_week } = comparison;

    const renderWeekColumn = (title, weekData) => (
        <div className="week-column">
            <h4 className="week-title">{title}</h4>
            <div className="week-stats">
                <div className="week-stat-item">
                    <span>Completed</span>
                    <span className="stat-percent green-text">{weekData.completed_percent.toFixed(1)}% ({weekData.completed_count})</span>
                </div>
                <div className="week-stat-item">
                    <span>In Progress</span>
                    <span className="stat-percent yellow-text">{weekData.in_progress_percent.toFixed(1)}% ({weekData.in_progress_count})</span>
                </div>
                <div className="week-stat-item">
                    <span>Failed</span>
                    <span className="stat-percent red-text">{weekData.failed_percent.toFixed(1)}% ({weekData.failed_count})</span>
                </div>
                <div className="week-stat-item">
                    <span>Not Started</span>
                    <span className="stat-percent blue-text">{weekData.not_started_percent.toFixed(1)}% ({weekData.not_started_count})</span>
                </div>
                
                <hr style={{ border: 'none', borderTop: '1px solid var(--border-color)', margin: '0.5rem 0' }} />
                
                {/* --- APPLIED FIX: Consistent format for the Delayed metric --- */}
                <div className="week-stat-item">
                    <span>Delayed</span>
                    <span className="stat-percent red-text">{(weekData.delayed_percent || 0).toFixed(1)}% ({weekData.delayed_count || 0})</span>
                </div>
                <div className="week-stat-item" style={{ marginTop: '0.25rem', fontWeight: '500' }}>
                    <span>Total Interfaces</span>
                    <span className="stat-percent">{weekData.total_interfaces}</span>
                </div>
            </div>
        </div>
    );

    return (
        <div className="weekly-comparison-container content-card">
            {renderWeekColumn("Last Week", last_week)}
            <div className="comparison-arrow">&#8635;</div>
            {renderWeekColumn("This Week", current_week)}
        </div>
    );
};

const TopIssuesSection = ({ issues, isLoading }) => {
    if (isLoading) return null;

    const { top_disapproved = [], top_reopened = [] } = issues || {};

    if (top_disapproved.length === 0 && top_reopened.length === 0) {
        return (
            <div className="kpi-section content-card">
                <h2 className="kpi-section-title">Interface Hotspots</h2>
                <div className="issue-card-body" style={{ marginTop: '0.5rem', paddingLeft: '1vw' }}>
                    <p className="no-errors-found" style={{ fontStyle: 'normal' }}>No recurring issues found for this project.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="kpi-section content-card">
            <h2 className="kpi-section-title">Interface Hotspots</h2>
            <div className="hotspots-container">
                <div className="hotspot-section">
                    <h3 className="hotspot-title">Top 5 Most Disapproved Interfaces</h3>
                    {top_disapproved.length > 0 ? (
                        <div className="top-issues-grid">
                            {top_disapproved.map((issue) => (
                                <div key={issue.interface_name} className="issue-card">
                                    <div className="issue-card-header">
                                        {/* --- APPLIED FIX: Formatting the interface name --- */}
                                        <h4 className="issue-interface-name">{formatInterfaceName(issue.interface_name)}</h4>
                                        <div className="reopening-count disapproval" title="Total Disapprovals">
                                            <FaExclamationTriangle /> {issue.disapproval_count}
                                        </div>
                                    </div>
                                    <div className="issue-card-body">
                                        <h5 className="top-errors-title">Most Common Error Types:</h5>
                                        {issue.top_errors.length > 0 ? (
                                            <ul className="error-list">
                                                {(() => {
                                                    // 1. Flatten categories from the JSON payloads
                                                    let allCategories = [];
                                                    (issue.top_errors || []).forEach(error => {
                                                        try {
                                                            const parsed = JSON.parse(error.error_message);
                                                            if (Array.isArray(parsed)) {
                                                                parsed.forEach(cat => {
                                                                    allCategories.push({
                                                                        title: cat.category.replace(/:$/, ''), // Clean trailing colons
                                                                        examples: cat.patterns.slice(0, 3).map(pat => {
                                                                            const rowStr = pat.rows && pat.rows.length > 0 ? `Row ${pat.rows[0]}: ` : '';
                                                                            const cleanDesc = (pat.description || '').replace(/^:\s*/, '');
                                                                            return `${rowStr}${cleanDesc}`;
                                                                        }),
                                                                        count: error.count
                                                                    });
                                                                });
                                                            } else {
                                                                throw new Error("Not a JSON array");
                                                            }
                                                        } catch (e) {
                                                            // Legacy plain-text fallback
                                                            allCategories.push({
                                                                title: (error.error_message || "Unknown Error").split('\n')[0].substring(0, 60),
                                                                examples: error.examples ? error.examples.slice(0, 3) : [],
                                                                count: error.count
                                                            });
                                                        }
                                                    });

                                                    if (allCategories.length === 0) return <li className="error-item">No detailed errors available.</li>;

                                                    // 2. Render the top 3 categories using your EXACT original styling
                                                    return allCategories.slice(0, 3).map((catData, index) => (
                                                        <li key={index} className="error-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
                                                                <span className="error-item-message" style={{ fontWeight: '600', paddingRight: '1rem', display: 'flex', gap: '0.5rem' }}>
                                                                    <span style={{ color: 'var(--danger-color)', opacity: 0.8 }}>{index + 1}.</span>
                                                                    <span>{catData.title}</span>
                                                                </span>
                                                                <span className="error-count" style={{ flexShrink: 0 }}>
                                                                    in {catData.count} upload{catData.count > 1 ? 's' : ''}
                                                                </span>
                                                            </div>
                                                            {catData.examples && catData.examples.length > 0 && (
                                                                <div style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: 'var(--dark-bg)', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                                                                    <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--light-text)', fontWeight: '600', marginBottom: '0.5rem', display: 'block' }}>Examples:</span>
                                                                    <ul style={{ margin: 0, paddingLeft: '1.2rem', color: 'var(--light-text)', fontSize: '0.8rem', listStyleType: 'circle' }}>
                                                                        {catData.examples.map((ex, exIdx) => (
                                                                            <li key={exIdx} style={{ marginBottom: '0.3rem', wordBreak: 'break-word', fontFamily: 'monospace' }}>
                                                                                {ex}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                        </li>
                                                    ));
                                                })()}
                                            </ul>
                                        ) : (
                                            <p className="no-errors-found">No validation errors recorded.</p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : <p className="no-hotspots-message">No interfaces with disapprovals found.</p>}
                </div>

                <div className="hotspot-section">
                    <h3 className="hotspot-title">Top 5 Most Reopened Interfaces</h3>
                    {top_reopened.length > 0 ? (
                        <div className="reopened-card">
                            <ul className="reopened-list">
                                {top_reopened.map((item, index) => (
                                    <li key={item.interface_name} className="reopened-item" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                        <span style={{ color: 'var(--light-text)', fontWeight: 'bold', fontSize: '1.1rem' }}>{index + 1}.</span>
                                        {/* --- APPLIED FIX: Formatting the interface name --- */}
                                        <span className="reopened-name" style={{ flexGrow: 1 }}>{formatInterfaceName(item.interface_name)}</span>
                                        <span className="reopened-count"><FaSyncAlt /> {item.reopening_count} times</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : <p className="no-hotspots-message">No reopened interfaces found.</p>}
                </div>
            </div>
        </div>
    );
};


// --- MAIN PAGE COMPONENT ---
export default function KpiDashboard() {
    const navigate = useNavigate();

    const { user, selectedProjectName } = useAuth();
    const userProjects = user?.projects || [];

    const [kpiData, setKpiData] = useState(null);
    const [topIssuesData, setTopIssuesData] = useState(null);
    const [statusOverviewData, setStatusOverviewData] = useState(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!selectedProjectName) {
            setKpiData(null); setTopIssuesData(null); setStatusOverviewData(null);
            if (user) { setLoading(false); } else { setLoading(true); }
            return;
        }
        const fetchDashboardData = async () => {
            setLoading(true);
            setError(null);
            try {
                const [kpiResponse, topIssuesResponse, overviewResponse] = await Promise.all([
                    axiosInstance.get(`/api/projects/${selectedProjectName}/kpi-dashboard`),
                    axiosInstance.get(`/api/projects/${selectedProjectName}/kpi/top-issues`),
                    axiosInstance.get(`/api/projects/${selectedProjectName}/status-overview`)
                ]);
                setKpiData(kpiResponse.data);
                setTopIssuesData(topIssuesResponse.data);
                setStatusOverviewData(overviewResponse.data.interfaces);
                setLoading(false);
            } catch (err) {
                if (err.response?.status === 401) return;

                setError(err.response?.data?.detail || 'Failed to load KPI data.');
                setKpiData(null);
                setTopIssuesData(null);
                setStatusOverviewData(null);
                setLoading(false);
            }
        };
        if (user) { fetchDashboardData(); }
    }, [selectedProjectName, user]);

    if (!user) return <LoadingSpinner />;

    return (
        <div className="admin-page-container">
            <Header />

            <main className="admin-main-content">
                <div className="page-header">
                    <button onClick={() => navigate('/home')} className="back-button">
                        <FaArrowLeft /> Back
                    </button>

                    <div className="header-title-group">
                        <h1 className="page-title">Project KPI Dashboard</h1>
                        {selectedProjectName && (
                            <div className="project-context-inline">
                                <span className="separator">|</span>
                                <span className="context-label">Project:</span>
                                <span className="project-highlight">{selectedProjectName}</span>
                            </div>
                        )}
                    </div>
                </div>

                {loading ? (
                    <LoadingSpinner />
                ) : error ? (
                    <ErrorMessage message={error} />
                ) : !selectedProjectName && userProjects.length > 0 ? (
                    <div className="info-message"><p>Please select a project from the header to view its KPIs.</p></div>
                ) : !selectedProjectName && userProjects.length === 0 ? (
                    <div className="info-message"><p>You are not assigned to any projects.</p></div>
                ) : kpiData ? (
                    <div className="kpi-layout">
                        <div className="kpi-section-wrapper">
                            <h2 className="kpi-section-title">Overall Project Progress</h2>
                            <KpiOverallProgressBar progress={kpiData.overall_progress} />
                        </div>

                        <TopIssuesSection issues={topIssuesData} isLoading={loading} />

                        <div className="kpi-section-wrapper">
                            <h2 className="kpi-section-title">Weekly Progress Comparison</h2>
                            <KpiWeeklyComparison comparison={kpiData.weekly_comparison} />
                        </div>

                        <div className="kpi-section-wrapper">
                            <h2 className="kpi-section-title">Status by Drop</h2>
                            <div className="drop-summary-grid">
                                {kpiData.drop_summary.map(drop => (
                                    <KpiDropSummaryTable key={drop.drop_name} drop={drop} />
                                ))}
                            </div>
                        </div>

                        <div className="kpi-section content-card">
                            <h2 className="kpi-section-title">Interface Status Summary</h2>
                            <StatusOverviewTable overviewData={statusOverviewData} />
                        </div>
                    </div>
                ) : (
                    <div className="info-message">
                        <p>No KPI data available for {selectedProjectName}.</p>
                    </div>
                )}
            </main>

            <style>{`
                 /* --- Dark Theme Variables --- */
                :root {
                    --dark-bg: #1a202c; --card-bg: #2d3748; --text-color: #e2e8f0; --light-text: #a0aec0;
                    --green-accent: #48bb78; --border-color: #4a5568; --input-bg: #1a202c;
                    --error-color: #fca5a5; --error-bg: rgba(239, 68, 68, 0.1); --danger-color: #ef4444;
                    --success-color: #a7f3d0; --success-bg: rgba(52, 211, 153, 0.1);
                    --blue-accent: #60a5fa; --blue-bg: rgba(59, 130, 246, 0.1);
                    --yellow-accent: #fcd34d; --yellow-bg: rgba(245, 158, 11, 0.1);
                    --orange-accent: #fb923c; --orange-bg: rgba(251, 146, 60, 0.1);
                }

                /* --- Base Styles --- */
                .admin-page-container { background-color: var(--dark-bg); min-height: 100vh; color: var(--text-color); font-family: 'Inter', sans-serif; }
                .admin-main-content { max-width: 1400px; margin: 2rem auto; padding: 0 2rem; }
                .page-header { display: flex; align-items: center; gap: 1rem; margin-bottom: 2rem; padding-bottom: 1rem; border-bottom: 1px solid var(--border-color); }
                .back-button { background: var(--card-bg); color: var(--text-color); border: 1px solid var(--border-color); padding: 0.5rem 1rem; border-radius: 6px; font-weight: 600; cursor: pointer; transition: background-color 0.2s; display: flex; align-items: center; gap: 0.5rem; flex-shrink: 0; }
                .back-button:hover { background: #4a5568; }
                .header-title-group { display: flex; align-items: center; flex-grow: 1; flex-wrap: wrap; gap: 1rem; }
                .page-title { font-size: 2rem; font-weight: 700; color: var(--text-color); margin: 0; line-height: 1; white-space: nowrap; }
                .project-context-inline { display: flex; align-items: center; font-size: 1rem; color: var(--light-text); font-weight: 500; white-space: nowrap; }
                .separator { margin-right: 1rem; color: var(--border-color); font-weight: 300; font-size: 1.2rem; position: relative; top: -1px; }
                .context-label { margin-right: 0.5rem; }
                .project-highlight { color: var(--green-accent); font-weight: 600; }
                .kpi-layout { display: grid; gap: 2.5rem; } 

                 /* --- Card & Section Styles --- */
                 .content-card { 
                    background-color: var(--card-bg); border: 1px solid var(--border-color);
                    border-radius: 12px; padding: 1.5rem 2rem;
                    box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                 }
                 .kpi-section-wrapper { 
                     display: flex; flex-direction: column; gap: 1rem; 
                 }
                 .kpi-section-title {
                     font-size: 1.3rem; font-weight: 600; color: var(--text-color);
                     margin: 0 0 0.5rem 0; 
                 }

                 /* Overall Progress Bar */
                 .kpi-summary-header { display: flex; justify-content: flex-end; gap: 2rem; padding: 0 0.5rem 1rem 0.5rem; border-bottom: 1px solid var(--border-color); margin-bottom: 1rem; }
                 .summary-item { display: flex; align-items: baseline; gap: 0.5rem; }
                 .summary-label { font-size: 0.9rem; color: var(--light-text); }
                 .summary-value { font-size: 1.3rem; font-weight: 700; }
                 .summary-value.total { color: var(--blue-accent); }
                 .summary-value.delayed { color: var(--error-color); }
                 .progress-bar-container { display: flex; width: 100%; height: 2rem; border-radius: 8px; overflow: hidden; background-color: var(--input-bg); font-size: 0.85rem; font-weight: 600; color: var(--dark-bg); margin-bottom: 1rem; }
                 .progress-bar-segment { height: 100%; transition: width 0.5s ease-in-out; display: flex; align-items: center; justify-content: center; text-shadow: none; white-space: nowrap; }
                 .progress-legend { display: flex; justify-content: flex-start; gap: 1.5rem; padding: 0.5rem; flex-wrap: wrap; }
                 .legend-item { display: flex; flex-direction: column; align-items: flex-start; }
                 .legend-main { display: flex; align-items: center; gap: 0.5rem; }
                 .legend-dot { width: 0.8rem; height: 0.8rem; border-radius: 50%; }
                 .legend-label { font-size: 0.9rem; color: var(--light-text); }
                 .legend-sublabel { font-size: 0.8rem; color: var(--error-color); padding-left: 1.3rem; font-style: italic; }

                 /* Progress Bar Colors */
                 .green { background-color: var(--green-accent); }
                 .yellow { background-color: var(--yellow-accent); }
                 .red { background-color: var(--danger-color); color: var(--text-color); } 
                 .blue { background-color: var(--blue-accent); }

                 /* Top Issues Section */
                .hotspots-container { display: grid; grid-template-columns: 2fr 1fr; gap: 2rem; }
                @media (max-width: 900px) { .hotspots-container { grid-template-columns: 1fr; } }
                .hotspot-title { font-size: 1.1rem; font-weight: 600; color: var(--light-text); margin: 0 0 1rem 0; padding-bottom: 0.75rem; border-bottom: 1px solid var(--border-color); }
                .top-issues-grid { display: grid; grid-template-columns: 1fr; gap: 1rem; }
                .issue-card { border: 1px solid var(--border-color); border-radius: 8px; background-color: var(--input-bg); overflow: hidden; }
                .issue-card-header { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 1rem; border-bottom: 1px solid var(--border-color); background-color: var(--card-bg); }
                .issue-interface-name { font-size: 1rem; font-weight: 600; color: var(--text-color); margin: 0; }
                .reopening-count { font-size: 1rem; font-weight: 700; display: flex; align-items: center; gap: 0.3rem; }
                .reopening-count.disapproval { color: var(--error-color); }
                .issue-card-body { padding: 1rem; }
                .top-errors-title { font-size: 0.8rem; font-weight: 600; color: var(--light-text); margin: 0 0 0.75rem 0; text-transform: uppercase; }
                .error-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.5rem; }
                .error-item { display: flex; justify-content: space-between; align-items: center; gap: 1rem; font-size: 0.875rem; background-color: var(--error-bg); padding: 0.75rem; border-radius: 6px; border: 1px solid var(--danger-color); border-left: 4px solid var(--danger-color); }
                .error-item-message { color: var(--error-color); word-break: break-word; flex-grow: 1; }
                .error-count { flex-shrink: 0; font-weight: 600; color: var(--error-color); background-color: var(--card-bg); padding: 0.25rem 0.6rem; border-radius: 99px; font-size: 0.8rem; white-space: nowrap; border: 1px solid var(--danger-color); }
                .no-errors-found, .no-hotspots-message { font-size: 0.9rem; color: var(--light-text); padding: 1rem 0; font-style: italic; }
                .reopened-card { background-color: var(--input-bg); border: 1px solid var(--border-color); border-radius: 8px; padding: 1rem; }
                .reopened-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 0.75rem; }
                .reopened-item { display: flex; justify-content: space-between; align-items: center; font-size: 0.9rem; background-color: var(--card-bg); padding: 0.75rem 1rem; border-radius: 6px; border: 1px solid var(--border-color);}
                .reopened-name { font-weight: 500; color: var(--text-color); }
                .reopened-count { font-weight: 700; color: var(--blue-accent); display: flex; align-items: center; gap: 0.3rem; }

                 /* Drop Summary */
                .drop-summary-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 1.5rem; }
                .drop-summary-title { font-weight: 600; color: var(--text-color); margin: 0 0 1rem 0; padding-bottom: 0.75rem; border-bottom: 1px solid var(--border-color); font-size: 1.1rem; }
                .drop-summary-table { width: 100%; border-collapse: collapse; }
                .drop-summary-table td { padding: 0.6rem 0.5rem; font-size: 0.9rem; border-top: 1px solid var(--border-color); color: var(--light-text); }
                .drop-summary-table tr:first-child td { border-top: none; }
                .drop-summary-table td:nth-child(1) { display: flex; align-items: center; } 
                .drop-summary-table td:nth-child(2) { text-align: right; font-weight: 500; color: var(--text-color); }
                .drop-summary-table td:nth-child(3) { text-align: right; font-weight: 600; color: var(--light-text); }

                 /* Weekly Comparison */
                .weekly-comparison-container { display: flex; justify-content: center; align-items: stretch; gap: 2rem; flex-wrap: wrap; }
                .week-column { border: 1px solid var(--border-color); border-radius: 8px; padding: 1.5rem; width: 280px; background-color: var(--input-bg); }
                .week-title { font-weight: 600; text-align: center; margin: 0 0 1rem 0; color: var(--light-text); font-size: 1.1rem; }
                .week-stats { display: flex; flex-direction: column; gap: 0.75rem; }
                .week-stat-item { display: flex; justify-content: space-between; font-size: 0.9rem; color: var(--light-text); }
                .stat-percent { font-weight: 600; }
                .comparison-arrow { font-size: 2rem; color: var(--border-color); align-self: center; }

                 /* Text Colors */
                .green-text { color: var(--success-color); }
                .yellow-text { color: var(--yellow-accent); }
                .red-text { color: var(--error-color); }
                .blue-text { color: var(--blue-accent); }

                 /* --- Status Overview Table --- */
                /* --- Status Overview Table --- */
                .table-wrapper { 
                    width: 100%; 
                    overflow-x: auto; 
                }
                .styled-table.overview-table { 
                    width: 100%; /* This is the magic line that forces full width */
                    border-collapse: collapse; 
                    text-align: left;
                }
                .styled-table.overview-table th {
                    border-bottom: 1px solid var(--border-color); /* Adds a nice separator under headers */
                    color: var(--light-text);
                    font-weight: 600;
                }
                .styled-table.overview-table th, .styled-table.overview-table td { 
                    padding: 0.75rem; 
                    white-space: nowrap; 
                    border-bottom: 1px solid var(--border-color); /* Separates the rows */
                }
                .styled-table.overview-table td:first-child, .styled-table.overview-table td:nth-child(2), .styled-table.overview-table td:nth-child(3) { 
                    white-space: normal; 
                }
                .status-badge { display: inline-block; padding: 0.25rem 0.75rem; border-radius: 9999px; font-weight: 600; font-size: 0.8rem; white-space: nowrap; text-transform: capitalize; }
                .status-badge.status-completed { background-color: var(--success-bg); color: var(--success-color); }
                .status-badge.status-in-progress { background-color: var(--yellow-bg); color: var(--yellow-accent); }
                .status-badge.status-not-started { background-color: var(--blue-bg); color: var(--blue-accent); }
                .status-badge.status-failed-validation { background-color: var(--error-bg); color: var(--error-color); }
                .status-badge.status-unknown { background-color: var(--border-color); color: var(--text-color); }

                 /* Utility & Message Classes */
                 .loading-container { display: flex; flex-direction: column; justify-content: center; align-items: center; padding: 3rem; color: var(--light-text); }
                 .loading-spinner { width: 40px; height: 40px; margin: 0 auto 1rem; border: 4px solid var(--border-color); border-top-color: var(--green-accent); border-radius: 50%; animation: spin 1s linear infinite; }
                 @keyframes spin { to { transform: rotate(360deg); } }
                 .form-feedback.error { background-color: var(--error-bg); color: var(--error-color); padding: 1rem; border-radius: 8px; text-align: center; border: 1px solid var(--danger-color); margin-bottom: 1.5rem; }
                 .info-message {
                     background-color: var(--card-bg); border: 1px solid var(--border-color);
                     color: var(--light-text); padding: 1.5rem; border-radius: 8px;
                     text-align: center; margin-top: 2rem;
                 }

                 /* Responsive */
                 @media (max-width: 768px) {
                     .admin-main-content { padding: 0 1rem; }
                     .kpi-section-title { font-size: 1.1rem; }
                     .drop-summary-grid { grid-template-columns: 1fr; } 
                 }
            `}</style>
        </div>
    );
}